import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaStar } from 'react-icons/fa';
import { RatingStarsFa, clampRating0to5 } from '../../utils/ratingStars';

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const ReviewsSection = ({
  reviews = [],
  renderStars: renderStarsProp,
  onReviewSubmit,
  showReviewForm = false,
  setShowReviewForm = () => {},
  reviewForm = { rating: 5, comment: '' },
  setReviewForm = () => {},
  onOpenReviewForm
}) => {
  const { t } = useTranslation();
  const renderStars = renderStarsProp || ((rating) => <RatingStarsFa rating={rating} size="sm" />);
  const handleToggleOrOpen = () => {
    if (onOpenReviewForm) {
      onOpenReviewForm();
    } else {
      setShowReviewForm(!showReviewForm);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onReviewSubmit) {
      onReviewSubmit(reviewForm);
    }
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h3 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(22px, 2.8vw, 30px)', letterSpacing: '-0.02em' }}>
          {t('restaurant.customerReviews', { count: reviews.length })}
        </h3>
        <button
          type="button"
          onClick={handleToggleOrOpen}
          style={{ border: 'none', cursor: 'pointer', background: PRIMARY, color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', padding: '11px 22px', borderRadius: '999px' }}
        >
          {t('restaurant.leaveReview')}
        </button>
      </div>

      {/* Formulaire d'avis */}
      {showReviewForm && (
        <div style={{ background: '#FCFBF5', borderRadius: '24px', padding: '24px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <h4 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '19px' }}>
            {t('restaurant.yourReview')}
          </h4>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <span style={{ display: 'block', fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)', marginBottom: '8px' }}>
                {t('restaurant.rating')}
              </span>
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }, (_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setReviewForm({ ...reviewForm, rating: index + 1 })}
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
                  >
                    <FaStar
                      size={22}
                      className={index < reviewForm.rating ? 'text-namke-mustard' : 'text-gray-300'}
                    />
                  </button>
                ))}
              </div>
            </div>

            <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>
                {t('restaurant.comment')}
              </span>
              <textarea
                value={reviewForm.comment}
                onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                style={{ border: '1px solid rgba(5,16,4,0.14)', borderRadius: '20px', padding: '14px 18px', fontFamily: 'Poppins, sans-serif', fontSize: '15px', background: '#FFFFFF', color: '#051004', resize: 'vertical', outline: 'none' }}
                rows={4}
                placeholder={t('restaurant.shareYourExperience')}
                required
              />
            </label>

            <div className="flex gap-3">
              <button
                type="submit"
                style={{ border: 'none', cursor: 'pointer', background: PRIMARY, color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', padding: '11px 22px', borderRadius: '999px' }}
              >
                {t('restaurant.publishReview')}
              </button>
              <button
                type="button"
                onClick={() => setShowReviewForm(false)}
                style={{ border: '1px solid rgba(5,16,4,0.14)', background: 'transparent', cursor: 'pointer', color: '#051004', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', padding: '11px 22px', borderRadius: '999px' }}
              >
                {t('restaurant.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Grille compacte des avis */}
      {reviews.length === 0 ? (
        <div style={{ background: '#FBF9D9', borderRadius: '22px', padding: '32px', textAlign: 'center' }}>
          <p style={{ margin: 0, color: 'rgba(5,16,4,0.55)', fontSize: '14.5px' }}>{t('restaurant.noReviewsYet')}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', gap: '12px' }}>
          {reviews.map((review, index) => (
            <div key={index} style={{ background: '#FBF9D9', borderRadius: '18px', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-0.5">{renderStars(clampRating0to5(review.rating ?? 0))}</span>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', color: 'rgba(5,16,4,0.5)', whiteSpace: 'nowrap' }}>
                  {new Date(review.created_at || Date.now()).toLocaleDateString('fr-FR')}
                </span>
              </div>
              <p style={{ margin: 0, fontSize: '13.5px', lineHeight: 1.5, color: 'rgba(5,16,4,0.78)', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {review.comment || review.content || t('restaurant.noComment')}
              </p>
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, color: PRIMARY }}>
                {review.user_name || t('restaurant.anonymousUser')}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsSection;
